import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { theme } from '../../../../theme/theme'
import { Avatar } from '../../../common/Avatar'
import { ListCard } from '../../../common/ListCard'
import { Option } from '../../../common/Dropdown'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { useModal } from '../../../../hooks/useModal'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { reverseUrl } from '../../../../navigation/reverseUrl'
import { IFacilityModel } from '../../../../interfaces/models/facilities.interfaces'
import { deleteFacility } from '../../../../api/facilities'

interface FacilityListItemProps {
  facility: IFacilityModel
  userIsAdmin?: boolean
}

export const FacilityListItem = ({
  facility,
  userIsAdmin,
}: FacilityListItemProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const {
    show: showConfirmationModal,
    open: openConfirmationModal,
    close: closeConfirmationModal,
  } = useModal()
  const navigate = useNavigate()

  const navigateToCreateUpdateFacilityView = (): void => {
    navigate(
      reverseUrl('society:facilities-update', {
        id: facility.societyId,
        facilityId: facility._id,
      })
    )
  }

  const dropdownOptions: Option[] | undefined = userIsAdmin
    ? [
        {
          value: 'edit',
          label: translate('common.actions.edit'),
          onClick: navigateToCreateUpdateFacilityView,
        },
        {
          value: 'remove',
          label: translate('common.actions.delete'),
          onClick: openConfirmationModal,
          destructive: true,
        },
      ]
    : undefined

  const { mutate: mutateDeleteFacility } = useMutation({
    mutationFn: deleteFacility,
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('createUpdateFacility.flashMessage.deleteFacilitySuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('createUpdateFacility.flashMessage.deleteFacilityFailure')
      )
    },
  })

  const onDeleteFacility = async (): Promise<void> => {
    mutateDeleteFacility(facility._id)
  }

  return (
    <ListCard
      toPath={reverseUrl('society:facilities-detail', {
        id: facility.societyId,
        facilityId: facility._id,
      })}
      dropdownOptions={dropdownOptions}
    >
      <>
        <div className="flex items-center">
          <Avatar mediaId={facility?.coverPhotoId} placeholder="booking" />
          <p style={theme.textVariants.baseBold} className="ml-4 line-clamp-1">
            {facility.name}
          </p>
        </div>
        <ConfirmationModal
          title={translate(
            'createUpdateFacility.confirmationModal.deleteTitle'
          )}
          description={translate(
            'createUpdateFacility.confirmationModal.deleteDescription'
          )}
          show={showConfirmationModal}
          close={closeConfirmationModal}
          onConfirm={onDeleteFacility}
          deleteMode
        />
      </>
    </ListCard>
  )
}
